import useSWR from 'swr';

import {
  GetGameDocumentContentAsync,
  GetGameDocumentLatestAsync
} from '../../services/json-document';
import { GetThemeAsync } from '../../services/games';
import { GameSettingOverride } from '../../types/event';
import { GetResourceById } from '../../utils/game-document';
import { addMissingColor } from '../../pages/game-designer/assets/theme';
import { GetEventPlaylistGameSettingOverride } from '../../services/events';

export const useGameSettingOverride = (
  settingOverride?: GameSettingOverride,
  eventId?: string,
  playlistId?: number,
  gameId?: number
) => {
  const {
    data: override,
    isLoading: overrideLoading,
    error: overrideError
  } = useSWR(
    !settingOverride && eventId && playlistId
      ? ['gameSettingOverride', eventId, playlistId]
      : null,
    ([, eId, pId]) => GetEventPlaylistGameSettingOverride(parseInt(eId), pId),
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );

  const {
    data: gameDoc,
    isLoading: docLoading,
    error: docError
  } = useSWR(
    !settingOverride && !override && gameId ? ['gameDocument', gameId] : null,
    async ([, id]) => {
      const latest = await GetGameDocumentLatestAsync(id);
      return await GetGameDocumentContentAsync(id, latest.id!);
    },
    { revalidateOnFocus: false }
  );

  const { data: theme, error: themeError } = useSWR(
    !settingOverride && gameDoc && gameId ? ['theme', gameId] : null,
    async ([, id]) => {
      const theme = await GetThemeAsync(id);
      theme.colors = addMissingColor(theme.colors ?? []);
      return theme;
    },
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );

  const hasOverride = !!settingOverride || !!override;
  const isLoading = !hasOverride && docLoading;
  const isError = !hasOverride && !!docError;

  if (settingOverride)
    return { isLoading: false, isError: false, content: settingOverride };

  if (override)
    return {
      isLoading: overrideLoading,
      isError: !!overrideError,
      content: override
    };

  if (!gameDoc) return { isLoading, isError, content: undefined };

  const overview = {
    logoResId: gameDoc.overview?.logoResId ?? '',
    titleResId: gameDoc.overview?.titleResId ?? '',
    longDescriptionResId: gameDoc.overview?.longDescriptionResId ?? '',
    medias: gameDoc.overview?.medias ?? []
  };

  const settings = {
    preGame: gameDoc.settings?.preGame,
    inGame: gameDoc.settings?.inGame
  };

  const resources = [
    GetResourceById(gameDoc, overview.logoResId),
    GetResourceById(gameDoc, overview.titleResId),
    GetResourceById(gameDoc, overview.longDescriptionResId),
    ...(overview.medias || []).map((media) =>
      GetResourceById(gameDoc, media.value)
    )
  ].filter((res) => !!res);

  const content = {
    overview,
    resources,
    settings,
    ...(theme && !themeError && { theme })
  } as GameSettingOverride;

  return { isLoading, isError, content };
};
